import { MouseEvent, useCallback, useState } from 'react';
import { Button } from '@/components/ui/button.tsx';
import { backLinkClassNames } from '@/components/ui/utils';
import { ResponsiveDialog } from '@/components/ResponsiveDialog/ResponsiveDialog.tsx';
import { useIsDesktop } from '@/hooks/useIsDesktop.ts';
import { trackingAttributes, type Tracking } from '@/lib/heap/trackingAttributes';
import { cn } from '@/lib/utils';

export interface BackButtonProps {
    text?: string;
    variant: 'wide' | 'narrow';
    confirmTitle?: string;
    confirmSubtitle?: string;
    confirmMessage?: string;
    confirmButtonText?: string;
    cancelButtonText?: string;
    onBack: () => void;
    /** Omit outer layout wrapper (e.g. pinned mobile toolbars) */
    inline?: boolean;
    className?: string;
    tracking?: Tracking;
}

export const BackButton = ({
    text = 'Back',
    variant,
    confirmTitle,
    confirmSubtitle,
    confirmMessage,
    confirmButtonText = 'Confirm',
    cancelButtonText = 'Cancel',
    onBack,
    inline = false,
    className,
    tracking,
}: BackButtonProps) => {
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const isDesktop = useIsDesktop();
    /** Below desktop breakpoint: compact back control (layout row + pinned toolbar). */
    const isMobileViewport = !isDesktop;

    const handleClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
        if (confirmTitle || confirmMessage) {
            // Safari does not focus a button on mouse click. Explicitly focus the trigger so the dialog can return focus to it on close.
            e.currentTarget.focus();
            setShowConfirmDialog(true);
        }
        else {
            onBack();
        }
    }, [confirmTitle, confirmMessage, onBack]);

    const handleConfirm = useCallback(() => {
        setShowConfirmDialog(false);
        onBack();
    }, [onBack]);

    const containerClasses = variant === 'narrow'
        ? 'w-full md:w-[768px] max-w-3xl mx-auto px-3 pt-3 lg:pt-5 lg:pb-1'
        : isDesktop
            ? 'container mx-auto px-6 pt-3 lg:pt-5 lg:pb-1'
            : 'w-full max-w-none px-6 pt-3 lg:pt-5 lg:pb-1';

    const buttonEl = (
        <Button
            variant="link"
            color={backLinkClassNames}
            size={inline || isMobileViewport ? 'sm' : 'lg'}
            onClick={handleClick}
            {...trackingAttributes(tracking)}
            className={cn(
                '!pl-0',
                'focus-visible:underline focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                inline || isMobileViewport
                    ? '!type-caption !font-normal !leading-[1.375rem] tracking-normal'
                    : '!type-caption',
                inline && 'self-start w-fit -ml-1 h-auto min-h-0 py-1',
                isMobileViewport && !inline && 'h-auto min-h-0 py-1',
                className,
            )}
        >
            <span className="material-symbols-rounded">arrow_back</span>
            <span>{text}</span>
        </Button>
    );

    return (
        <>
            {inline ? buttonEl : <div className={containerClasses}>{buttonEl}</div>}

            <ResponsiveDialog
                isOpen={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                title={confirmTitle}
                subtitle={confirmSubtitle}
                showCloseButton={true}
                forceModal={true}
                footer={(
                    <div className="flex justify-between gap-4">
                        <Button
                            onClick={handleConfirm}
                            variant="outline"
                            className="flex-1"
                        >
                            {confirmButtonText}
                        </Button>
                        <Button
                            onClick={() => setShowConfirmDialog(false)}
                            className="flex-1"
                        >
                            {cancelButtonText}
                        </Button>
                    </div>
                )}
            >
                {confirmMessage && <p className="text-sm">{confirmMessage}</p>}
            </ResponsiveDialog>
        </>
    );
};
